import { log } from '@shared/log.browser.ts';
import type { DresonRuleResolveEvent } from '@shared/types/Tilelist.ts';
import type { EventsIncoming } from '@tilelist/application/ports/EventsIncoming.ts';
import type { EventsOutgoing } from '@tilelist/application/ports/EventsOutgoing.ts';
import { Selection } from './NexusSelection.browser.ts';
import type { SelectionDependencies } from './NexusSelection.browser.ts';
import { createProductionSelectionDependencies } from './SelectionDependencies.browser.ts';

type SelectionLifecycleDependencies = {
    eventsIncoming: EventsIncoming;
    eventsOutgoing: EventsOutgoing;
    selection?: Omit<SelectionDependencies, 'eventsOutgoing'>;
};

export type SelectionLifecycle = (() => void) & {
    selection: Selection;
};

export function startSelection({
    eventsIncoming,
    eventsOutgoing,
    selection: selectionDependencies,
}: SelectionLifecycleDependencies): SelectionLifecycle {
    const selection = new Selection({
        ...(selectionDependencies ?? createProductionSelectionDependencies()),
        eventsOutgoing,
    });
    const unregister = selection.register();
    const handler = (event: DresonRuleResolveEvent) => {
        if (event.redirect || event.historic) {
            log.warn('Ignored legacy selection assignment; Dundee owns assignment internally');
            return;
        }

        try {
            const attributes = legacyAttributes(event);
            void selection.resolve({ attributes }).catch((error: unknown) => {
                eventsOutgoing.abortPageResolution(error);
            });
        } catch (error) {
            eventsOutgoing.abortPageResolution(error);
        }
    };

    const unsubscribe = eventsIncoming.onPageResolutionRequested(handler);

    log.debug('Legacy selection lifecycle is enabled.');
    return Object.assign(
        () => {
            unsubscribe();
            unregister();
        },
        { selection },
    );
}

function legacyAttributes(event: DresonRuleResolveEvent): Record<string, string | string[]> {
    const attributes: Record<string, string | string[]> = {};
    event.formData?.forEach((value, key) => {
        if (typeof value !== 'string') throw new Error(`Unsupported selection field "${key}"`);
        const existing = attributes[key];
        attributes[key] =
            existing === undefined ? value : Array.isArray(existing) ? [...existing, value] : [existing, value];
    });

    if (attributes.rule === undefined) attributes.rule = event.rule;
    const formSort = attributes.sortiertnach;
    const pageSort = event.page?.sortiertnach;
    if (pageSort !== undefined) {
        if (formSort === undefined) attributes.sortiertnach = pageSort;
        else if ((Array.isArray(formSort) ? formSort : [formSort]).some((value) => value !== pageSort)) {
            throw new Error('Conflicting sort order');
        }
    }
    return attributes;
}
